// Progression between nights: the Service Record. Every gun, grenade and class is open from the
// first ride (James, 2026-10-21), so what carries over is everything AROUND the guns: gun finishes,
// titles and opt-in harder contracts. No currency and no stat bumps (GAMEPLAY_LOOP §3, §6.6):
// a veteran has more ways to look and more ways to suffer, never more damage.
//
// Shared by client (counting, the record screen) and server (validating the look, pooling the
// posse's contracts). Pure data + functions; every number a designer would tune is in the tables
// below. Options page: "Unlocks and progression: options" (the project doc).

/** What the record counts. Posse stats credit every cowboy in the run; personal ones only you. */
export type Stat = 'runs' | 'orders' | 'islands' | 'bosses' | 'extracts' | 'kills' | 'dets' | 'revives';
export const STATS: Stat[] = ['runs', 'orders', 'islands', 'bosses', 'extracts', 'kills', 'dets', 'revives'];
export const STAT_LABEL: Record<Stat, [string, string]> = {
  runs: ['shift', 'shifts clocked'],
  orders: ['work order', 'work orders filled'],
  islands: ['island', 'islands cleared'],
  bosses: ['boss', 'bosses scrapped'],
  extracts: ['extraction', 'extractions'],
  kills: ['robot', 'robots scrapped'],
  dets: ['detonation', 'detonations'],
  revives: ['revive', 'teammates revived'],
};

// ------------------------------------------------------------------ rewards
/** Gun finishes recolour every gun you carry; the posse sees them (C2S.SetLook → roster). */
export const enum Finish { Blued = 0, Nickel, CaseHardened, Engraved, BoneGrip, Blackout, Gold }
export interface FinishDef { id: Finish; name: string; blurb: string }
export const FINISHES: FinishDef[] = [
  { id: Finish.Blued, name: 'Factory Blued', blurb: 'G.U.L.P. standard issue. Dark steel, brass where it counts.' },
  { id: Finish.Nickel, name: 'Nickel Plate', blurb: 'Bright plating that catches the sun. Robots notice you coming.' },
  { id: Finish.CaseHardened, name: 'Case-Hardened', blurb: 'Mottled straw and blue from the forge. A working gun with a story.' },
  { id: Finish.Engraved, name: 'Engraved Brass', blurb: 'Scrollwork on brass frames. The kind of gun Pop keeps behind the bar.' },
  { id: Finish.BoneGrip, name: 'Bone Grips', blurb: 'Ivory-white grips on blued steel. Old money.' },
  { id: Finish.Blackout, name: 'Blackout', blurb: 'Matte black, end to end. For cowboys who file their paperwork late.' },
  { id: Finish.Gold, name: 'Employee of the Year', blurb: 'Solid gold. G.U.L.P. deducted it from your wages.' },
];
export const FINISH_COUNT = FINISHES.length;

/**
 * Contracts are opt-in difficulty for the whole run, signed in the saloon (Pop's paperwork, or the
 * Service Record) before the posse saddles up. The posse can sign any contract that ANYONE riding
 * has earned; one at a time. sim/run.ts startRun applies it (contractEffect).
 */
export const enum Contract { Overtime = 0, Graveyard = 1, Takeover = 2 }
export interface ContractDef { id: Contract; name: string; blurb: string; live: boolean }
export const CONTRACTS: ContractDef[] = [
  { id: Contract.Overtime, name: 'Overtime', blurb: 'Skip the first village. The run starts in village 2.', live: true },
  { id: Contract.Graveyard, name: 'Graveyard Shift', blurb: 'Skip two villages. The run starts in village 3.', live: true },
  { id: Contract.Takeover, name: 'Hostile Takeover', blurb: 'Every robot is as tough as a village deeper, all run long.', live: true },
];
export const NO_CONTRACT = 255;

/** What a signed contract does to the run: the village it starts in, extra robot tiers (runTier units). */
export function contractEffect(c: number): { village: number; tiers: number } {
  if (c === Contract.Overtime) return { village: 1, tiers: 0 };
  if (c === Contract.Graveyard) return { village: 2, tiers: 0 };
  if (c === Contract.Takeover) return { village: 0, tiers: 2 };
  return { village: 0, tiers: 0 };
}

export type Reward =
  | { kind: 'finish'; id: Finish }
  | { kind: 'title'; name: string }
  | { kind: 'contract'; id: Contract };

// ------------------------------------------------------------------ the ladder
export interface Commendation {
  id: string;
  name: string;
  stat: Stat;
  n: number;
  /** 'posse' = everyone in the run gets it; 'you' = your own hands */
  scope: 'posse' | 'you';
  reward: Reward;
}

/** In the order a typical posse earns them (see the options page's grind table). */
export const COMMENDATIONS: Commendation[] = [
  { id: 'clocked_in', name: 'Clocked In', stat: 'orders', n: 3, scope: 'posse', reward: { kind: 'finish', id: Finish.Nickel } },
  { id: 'first_liq', name: 'First Liquidation', stat: 'islands', n: 1, scope: 'posse', reward: { kind: 'title', name: 'Liquidator' } },
  { id: 'two_step', name: 'Two-Step', stat: 'dets', n: 25, scope: 'you', reward: { kind: 'finish', id: Finish.CaseHardened } },
  { id: 'kin', name: 'Next of Kin', stat: 'revives', n: 5, scope: 'you', reward: { kind: 'title', name: 'Next of Kin' } },
  { id: 'scrap_value', name: 'Scrap Value', stat: 'bosses', n: 1, scope: 'posse', reward: { kind: 'finish', id: Finish.Engraved } },
  { id: 'punch_card', name: 'Punch Card', stat: 'orders', n: 20, scope: 'posse', reward: { kind: 'finish', id: Finish.BoneGrip } },
  { id: 'supper', name: 'Home by Supper', stat: 'extracts', n: 1, scope: 'posse', reward: { kind: 'contract', id: Contract.Overtime } },
  { id: 'repeat', name: 'Repeat Customer', stat: 'islands', n: 6, scope: 'posse', reward: { kind: 'title', name: 'Repeat Customer' } },
  { id: 'derby', name: 'Demolition Derby', stat: 'dets', n: 150, scope: 'you', reward: { kind: 'finish', id: Finish.Blackout } },
  { id: 'dealer', name: 'Scrap Dealer', stat: 'bosses', n: 4, scope: 'posse', reward: { kind: 'title', name: 'Scrap Dealer' } },
  { id: 'company_man', name: 'Company Man', stat: 'extracts', n: 3, scope: 'posse', reward: { kind: 'contract', id: Contract.Graveyard } },
  { id: 'punch_card2', name: 'Punch Card II', stat: 'orders', n: 60, scope: 'posse', reward: { kind: 'contract', id: Contract.Takeover } },
  { id: 'eoty', name: 'Employee of the Year', stat: 'extracts', n: 5, scope: 'posse', reward: { kind: 'finish', id: Finish.Gold } },
];

/** Rank title by commendations held. Always wearable once reached. */
export const RANKS: { at: number; name: string }[] = [
  { at: 0, name: 'Trainee' },
  { at: 2, name: 'Associate' },
  { at: 4, name: 'Shift Lead' },
  { at: 7, name: 'Floor Manager' },
  { at: 10, name: 'Regional Manager' },
  { at: 13, name: 'Vice President of Liquidation' },
];

// ------------------------------------------------------------------ the record
export interface Look { finish: Finish; title: string }

export interface Ledger {
  v: 1;
  stats: Record<Stat, number>;
  /** commendation id → unix ms earned */
  earned: Record<string, number>;
  look: Look;
}

export function blankLedger(): Ledger {
  const stats = {} as Record<Stat, number>;
  for (const s of STATS) stats[s] = 0;
  return { v: 1, stats, earned: {}, look: { finish: Finish.Blued, title: '' } };
}

/** Accept anything that came out of storage; drop what doesn't fit. */
export function sanitizeLedger(raw: unknown): Ledger {
  const l = blankLedger();
  if (!raw || typeof raw !== 'object') return l;
  const r = raw as Partial<Ledger>;
  for (const s of STATS) {
    const v = Number(r.stats?.[s]);
    if (Number.isFinite(v) && v > 0) l.stats[s] = Math.floor(v);
  }
  if (r.earned && typeof r.earned === 'object') {
    for (const c of COMMENDATIONS) if (Number(r.earned[c.id]) > 0) l.earned[c.id] = Number(r.earned[c.id]);
  }
  if (r.look) {
    const f = Number(r.look.finish);
    if (f >= 0 && f < FINISH_COUNT) l.look.finish = f;
    if (typeof r.look.title === 'string') l.look.title = r.look.title.slice(0, 40);
  }
  return l;
}

/** File every commendation whose bar is met. Returns the new ones (for the toast). */
export function settle(l: Ledger, now = Date.now()): Commendation[] {
  const fresh: Commendation[] = [];
  for (const c of COMMENDATIONS) {
    if (l.earned[c.id]) continue;
    if (l.stats[c.stat] >= c.n) { l.earned[c.id] = now; fresh.push(c); }
  }
  return fresh;
}

export const earnedCount = (l: Ledger) => COMMENDATIONS.reduce((n, c) => n + (l.earned[c.id] ? 1 : 0), 0);

export function rankOf(count: number) {
  let r = RANKS[0];
  for (const k of RANKS) if (count >= k.at) r = k;
  return r;
}
export function nextRank(count: number) {
  return RANKS.find((k) => k.at > count) ?? null;
}

export function finishOpen(l: Ledger, f: Finish) {
  if (f === Finish.Blued) return true;
  return COMMENDATIONS.some((c) => l.earned[c.id] && c.reward.kind === 'finish' && c.reward.id === f);
}
export function contractsMask(l: Ledger) {
  let m = 0;
  for (const c of COMMENDATIONS) if (l.earned[c.id] && c.reward.kind === 'contract') m |= 1 << c.reward.id;
  return m;
}
/** Wearable titles: every rank reached, every title commendation, plus any extra (achievement names). */
export function titlesOpen(l: Ledger, extra: string[] = []) {
  const n = earnedCount(l);
  const out = RANKS.filter((r) => n >= r.at).map((r) => r.name);
  for (const c of COMMENDATIONS) if (l.earned[c.id] && c.reward.kind === 'title') out.push(c.reward.name);
  for (const e of extra) if (!out.includes(e)) out.push(e);
  return out;
}
/** The title shown for you: your pick if you still hold it, else your rank. */
export function wornTitle(l: Ledger, extra: string[] = []) {
  const open = titlesOpen(l, extra);
  return l.look.title && open.includes(l.look.title) ? l.look.title : rankOf(earnedCount(l)).name;
}
export function wornFinish(l: Ledger) {
  return finishOpen(l, l.look.finish) ? l.look.finish : Finish.Blued;
}

export function rewardLabel(r: Reward) {
  if (r.kind === 'finish') return `Gun finish: ${FINISHES[r.id].name}`;
  if (r.kind === 'title') return `Title: ${r.name}`;
  return `Contract: ${CONTRACTS[r.id].name}`;
}

/** Every cowboy's record unlocks the lot (dev, demos, "?record=open"). */
export function openLedger(): Ledger {
  const l = blankLedger();
  for (const c of COMMENDATIONS) { l.earned[c.id] = 1; l.stats[c.stat] = Math.max(l.stats[c.stat], c.n); }
  return l;
}
